import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  ButtonBase,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  ExpandLess,
  ExpandMore,
  Remove as RemoveIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { locationLabel } from '../utils';
import HazardDot from '../../../components/HazardDot';

export default function ResultPhase({
  item,
  matchedLot,
  stockLevels,
  totalStock,
  workingLocation,
  onPickLocation,
  expiryDate,
  expired,
  isHazardous,
  hazardLevel,
  onScanAgain,
  onUseStock,
  onAddStock,
  onOpenMsds,
}) {
  const { t } = useTranslation(['inventory', 'common']);
  // Lead with the stock at the working location. When there is none there
  // but some elsewhere, open the list so the right location is one tap away.
  const here = workingLocation
    ? stockLevels.find(b => b.location_id === workingLocation.stock_location_id) ?? { quantity: 0 }
    : null;
  const stockElsewhere = here !== null && here.quantity <= 0 && totalStock > 0;
  const [showLocations, setShowLocations] = useState(stockElsewhere);

  if (!item) return null;
  const uom = item.uom ?? '';

  const headline = here
    ? { qty: here.quantity, label: t('inventory:scanner.stock_at', { uom, location: workingLocation.name }) }
    : { qty: totalStock, label: t('inventory:scanner.stock_total_label', { uom }) };
  const blocked = matchedLot && ['recalled', 'quarantined'].includes(matchedLot.status);

  const subtitle = [
    item.sku,
    matchedLot && t('inventory:scanner.lot_label', { lot: matchedLot.lot_number }),
    expiryDate && !expired && t('inventory:scanner.expires', { date: expiryDate }),
  ].filter(Boolean).join(' · ');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
      <Box sx={{ p: 1, pb: 12, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <IconButton onClick={onScanAgain} aria-label={t('inventory:scanner.scan_again')} sx={{ width: 48, height: 48 }}>
            <BackIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
            <Typography variant="h6" lineHeight={1.25} data-testid="item-name">
              <HazardDot level={hazardLevel} size={14} sx={{ mr: 2, verticalAlign: 'middle', position: 'relative', top: -2 }} />
              {item.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
          </Box>
        </Box>

        <Box sx={{ px: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {blocked && (
            <Alert severity="error" sx={{ fontWeight: 600 }}>
              {t('inventory:scanner.lot_status_warning', { status: t(`inventory:batches.filters.${matchedLot.status}`, matchedLot.status) })}
            </Alert>
          )}
          {expired && (
            <Alert severity="error" sx={{ fontWeight: 600 }}>
              {expiryDate ? t('inventory:scanner.expired_with_date', { date: expiryDate }) : t('inventory:scanner.expired')}
            </Alert>
          )}
          {!item.active && <Chip label={t('inventory:scanner.chip_inactive')} color="warning" size="small" sx={{ alignSelf: 'flex-start' }} />}
          {isHazardous && onOpenMsds && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<WarningIcon />}
              onClick={onOpenMsds}
              sx={{ alignSelf: 'flex-start', minHeight: 44 }}
            >
              {t('inventory:scanner.msds_button')}
            </Button>
          )}

          <Box sx={{ py: 1 }}>
            <Typography
              data-testid="stock-headline"
              sx={{ fontSize: 48, fontWeight: 700, lineHeight: 1 }}
              color={headline.qty > 0 ? 'text.primary' : 'error.main'}
            >
              {headline.qty}
            </Typography>
            <Typography variant="body1" color="text.secondary">{headline.label}</Typography>
          </Box>

          {(stockLevels.length > 1 || stockElsewhere) && (
            <Box>
              <Button
                onClick={() => setShowLocations(v => !v)}
                endIcon={showLocations ? <ExpandLess /> : <ExpandMore />}
                sx={{ px: 0, minHeight: 44 }}
              >
                {here
                  ? t('inventory:scanner.all_locations_with_total', { count: stockLevels.length, total: totalStock, uom })
                  : t('inventory:scanner.all_locations', { count: stockLevels.length })}
              </Button>
              <Collapse in={showLocations}>
                {stockElsewhere && (
                  <Typography variant="caption" color="text.secondary" component="p" sx={{ pb: 0.5 }}>
                    {t('inventory:scanner.location_switch_hint')}
                  </Typography>
                )}
                {stockLevels.map(b => {
                  const current = workingLocation && b.location_id === workingLocation.stock_location_id;
                  // Rows of real locations switch the working location; stock with no location can't be worked at
                  const pickable = b.location_id && !current && onPickLocation;
                  return (
                    <ButtonBase
                      key={b.location_id ?? 'none'}
                      disabled={!pickable}
                      onClick={() => onPickLocation(b.location_id)}
                      sx={{
                        width: '100%', display: 'flex', justifyContent: 'space-between', py: 1.25, px: 0.5,
                        borderBottom: 1, borderColor: 'divider', textAlign: 'left',
                        bgcolor: current ? 'action.selected' : undefined,
                      }}
                    >
                      <Typography variant="body2" fontWeight={current ? 600 : 400}>{locationLabel(b, t)}</Typography>
                      <Typography variant="body2" fontWeight={600} color={b.quantity > 0 ? 'text.primary' : 'error.main'}>
                        {b.quantity} {uom}
                      </Typography>
                    </ButtonBase>
                  );
                })}
              </Collapse>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1.5,
          p: 1.5,
          pb: 'calc(12px + env(safe-area-inset-bottom))',
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        <Button
          variant="contained"
          size="large"
          startIcon={<RemoveIcon />}
          onClick={onUseStock}
          disabled={blocked || expired}
          sx={{ minHeight: 60, borderRadius: 2, fontSize: '1.1rem' }}
        >
          {t('inventory:scanner.use_button')}
        </Button>
        <Button
          variant="outlined"
          color="success"
          size="large"
          startIcon={<AddIcon />}
          onClick={onAddStock}
          sx={{ minHeight: 60, borderRadius: 2, fontSize: '1.1rem', borderWidth: 2, '&:hover': { borderWidth: 2 } }}
        >
          {t('inventory:scanner.add_button')}
        </Button>
      </Box>
    </Box>
  );
}
